import { useState } from 'react';
import type { ObjectView } from '../model/table';
import type { Resolved } from '../presentation/bindings';
import { usePresentation } from '../presentation/PresentationContext';
import { Glyph } from '../presentation/Glyph';
import { seatColorVar } from './seatColors';
import { SvgImage } from '../art/SvgImage';

interface Props {
  object: ObjectView;
  zoneKind?: string | null;
  selectable: boolean;
  selected: boolean;
  size?: 'compact' | 'normal' | 'large';
  seatIndex: Map<string, number>;
  attached?: ObjectView[];
  onInspect: (id: string) => void;
}

const TOKEN_KINDS = new Set(['token', 'worker', 'marker', 'die']);

/**
 * Draws an object through its presentation template, or the default template for its kind.
 * Text never lives only in images: the accessible name is always the object's label.
 */
export function ObjectCard({ object, zoneKind = null, selectable, selected, size = 'normal', seatIndex, attached = [], onInspect }: Props) {
  const api = usePresentation();
  const target = api.pickTargets?.values.get(object.id);
  const isTarget = target !== undefined;
  const rendered = object.faceDown ? null : api.render(object, zoneKind);
  const shape = rendered?.shape ?? (TOKEN_KINDS.has(object.kind) ? 'token' : 'card');
  const ownerColor = object.owner ? seatColorVar(seatIndex.get(object.owner) ?? 0) : undefined;
  const shapeClass = shape === 'card' ? 'object-card' : shape === 'token' || shape === 'node' ? 'object-token' : 'object-tile';
  const classes = ['object', shapeClass, `object-shape-${shape}`, `object-${size}`];
  if (selectable || isTarget) classes.push('is-selectable');
  if (isTarget) classes.push('is-target');
  if (selected) classes.push('is-selected');
  if (object.faceDown) classes.push('is-face-down');
  const slots = rendered?.slots ?? {};
  const fill = slots.fill?.kind === 'color' ? slots.fill.value : undefined;
  const band = slots.band?.kind === 'color' ? slots.band.value : undefined;
  const style = { ...(ownerColor ? { '--owner-color': ownerColor } : {}), ...(fill ? { '--fill-color': fill } : {}), ...(band ? { '--band-color': band } : {}) } as React.CSSProperties;
  const activate = () => (isTarget ? api.pickTargets!.answer(target) : onInspect(object.id));
  const suffix = isTarget ? '. Choose this.' : selectable ? '. Has available moves.' : '';

  return (
    <button type="button" className={classes.join(' ')} aria-label={object.label + suffix} aria-pressed={selected} onClick={activate} style={style}>
      {object.faceDown ? <span className="object-back" aria-hidden="true" />
        : rendered ? <TemplateBody shape={shape} slots={slots} object={object} size={size} />
          : <DefaultBody object={object} size={size} />}
      {attached.length > 0 && (
        <span className="object-attached" aria-hidden="true">
          {attached.map(a => (
            <span key={a.id} className={`attached-${TOKEN_KINDS.has(a.kind) ? 'token' : 'card'}`}
              style={a.owner ? ({ '--owner-color': seatColorVar(seatIndex.get(a.owner) ?? 0) } as React.CSSProperties) : undefined} title={a.label} />
          ))}
        </span>
      )}
      {object.count !== null && object.count > 1 && <span className="object-count" aria-hidden="true">×{object.count}</span>}
      {(selectable || isTarget) && <span className="object-flag" aria-hidden="true" />}
    </button>
  );
}

function DefaultBody({ object, size }: { object: ObjectView; size: string }) {
  return (
    <>
      <span className="object-head">
        <span className="object-title">{object.title}</span>
        {object.points !== null && <span className="object-points" title="Points">{object.points}</span>}
      </span>
      {object.subtitle && size !== 'compact' && <span className="object-subtitle">{object.subtitle}</span>}
      {object.costs.length > 0 && (
        <span className="object-costs" aria-hidden="true">
          {object.costs.map(c => <span key={c.key} className="cost-chip"><span className="cost-key">{c.key}</span>{c.value}</span>)}
        </span>
      )}
    </>
  );
}

function TemplateBody({ shape, slots, object, size }: { shape: string; slots: Record<string, Resolved | null>; object: ObjectView; size: string }) {
  const title = slots.title ?? slots.label;
  const titleText = title?.kind === 'text' ? title.text : object.title;
  if (shape !== 'card') {
    return (
      <>
        {slots.art && <SlotArt value={slots.art} />}
        <span className="object-title object-title-token">{titleText}</span>
        {slots.badge && <span className="object-badge"><Slot value={slots.badge} /></span>}
      </>
    );
  }
  return (
    <>
      {slots.band && <span className="object-band" aria-hidden="true" />}
      {slots.art && size !== 'compact' && <SlotArt value={slots.art} />}
      <span className="object-head">
        <span className="object-title">{titleText}</span>
        {slots.corner && <span className="object-points"><Slot value={slots.corner} /></span>}
      </span>
      {slots.subtitle && size !== 'compact' && <span className="object-subtitle"><Slot value={slots.subtitle} /></span>}
      {slots.text && size === 'large' && <span className="object-text"><Slot value={slots.text} /></span>}
      {slots.cost && <span className="object-costs" aria-hidden="true"><Slot value={slots.cost} /></span>}
      {(slots.footer || slots.badge) && (
        <span className="object-footer">
          {slots.footer && <Slot value={slots.footer} />}
          {slots.badge && <span className="object-badge"><Slot value={slots.badge} /></span>}
        </span>
      )}
    </>
  );
}

/** Art that disappears when the image is missing; the text slots still carry the meaning. */
function SlotArt({ value }: { value: Resolved }) {
  const [failed, setFailed] = useState(false);
  if (value.kind === 'glyph') return <span className="object-art object-art-glyph"><Glyph name={value.name} size={28} /></span>;
  if (value.kind !== 'image' || failed) return null;
  return <span className="object-art"><img src={value.url} alt="" loading="lazy" onError={() => setFailed(true)} /></span>;
}

export function Slot({ value }: { value: Resolved }) {
  switch (value.kind) {
    case 'text': return <>{value.text}</>;
    case 'glyph': return <Glyph name={value.name} />;
    case 'image': return value.frame && value.sheet ? <svg className="icon-image" width={20} height={20} aria-hidden="true"
      viewBox={`${value.frame.x} ${value.frame.y} ${value.frame.w} ${value.frame.h}`} preserveAspectRatio="xMidYMid meet" overflow="hidden">
      <SvgImage href={value.url} x={0} y={0} width={value.sheet.w} height={value.sheet.h} preserveAspectRatio="none" />
    </svg> : <ImageIcon url={value.url} />;
    case 'color': return <span className="color-swatch" style={{ background: value.value }} />;
    case 'costs': return (
      <>
        {value.items.map(i => (
          <span key={i.key} className="cost-chip" title={i.label}>
            {i.icon ? <Slot value={i.icon} /> : <span className="cost-key">{i.label}</span>}{i.value}
          </span>
        ))}
      </>
    );
  }
}

function ImageIcon({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <Glyph name="circle" />;
  return <img className="icon-image" src={url} alt="" width={16} height={16} onError={() => setFailed(true)} />;
}
